import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Badge,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Settings as ManageIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Casino as CasinoIcon,
  Lock as LockIcon,
  Public as PublicIcon,
} from "@mui/icons-material";

import AllRequests from "./AllRequests.jsx";

function GameManager() {
  const hostGames = useSelector((state) => state.host.gamelist);
  const requests = useSelector((state) => state.requests.receivedRequests);
  const navigate = useNavigate();

  const getRequestCount = (gameId: string) => {
    if (!requests) return 0;
    return Object.keys(requests).filter(reqKey => 
      requests[reqKey].gameId === gameId && requests[reqKey].status === "pending"
    ).length;
  };

  const getStatusColor = (status: string, visible: boolean) => {
    if (!visible) return 'default';
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const getAccessIcon = (access: string) => {
    return access === 'Private' ? <LockIcon /> : <PublicIcon />;
  };

  const games = Object.keys(hostGames);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ManageIcon color="primary" />
          Manage Games
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View and manage all your hosted poker games
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <AllRequests />
      </Box>

      {games.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CasinoIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h5" color="textSecondary" gutterBottom>
              No games created yet
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Create your first poker game to get started
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<CasinoIcon />}
              onClick={() => navigate('/hostpanel')}
            >
              Create Game
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mb: 3 }}>
            Your Games ({games.length})
          </Typography>
          
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Game Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Access</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {games.map((gameKey) => {
                  const gameData = hostGames[gameKey];
                  const requestCount = getRequestCount(gameData.id);
                  
                  return (
                    <TableRow 
                      key={gameData.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/games/${gameData.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CasinoIcon color="primary" />
                          <Typography variant="body1" fontWeight="medium">
                            {gameData.gameName}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={gameData.type} 
                          size="small"
                          variant="outlined"
                          color={gameData.type === 'Tournament' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getAccessIcon(gameData.access)}
                          <Typography variant="body2">
                            {gameData.access}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip 
                            label={gameData.visible === false ? "Hidden" : gameData.status}
                            size="small"
                            color={getStatusColor(gameData.status, gameData.visible)}
                            icon={gameData.visible === false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          />
                        </Box>
                      </TableCell>
                      
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Manage Game">
                            <Badge badgeContent={requestCount} color="error">
                              <IconButton
                                color="primary"
                                onClick={() => navigate(`/manage/${gameData.id}`)}
                                size="small"
                              >
                                <ManageIcon />
                              </IconButton>
                            </Badge>
                          </Tooltip>
                          
                          <Tooltip title="Edit Game Details">
                            <IconButton
                              color="secondary"
                              onClick={() => navigate(`/editgame/${gameData.id}`)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

export default GameManager;
